import { useMemo } from 'react';
import {
  Button,
  Divider,
  IconButton,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import {
  AddIcon,
  TrashIcon,
} from '@fortress-technology-solutions/fortress-component-library/Icons';
import { SingleSelect } from '@fortress-technology-solutions/fortress-component-library/Molecules';
import {
  maritalStatuses,
  raceOptions,
} from '../../../components/LeaseAffordableApplicantFormCommonSections/configuration';
import useIntl from '../../../hooks/useIntl';
import {
  withConditionallyRequiredIcon,
  getIdentificationIconState,
  getIdentificationPairRules,
  getSsnFieldRules,
  getEmailFieldRules,
  getPhoneFieldRules,
  getApplicantTypeFieldRules,
  getRelationshipFieldRules,
  getStartDateValidate,
  getEndDateValidate,
  getDateOfBirthValidate,
  getAnnualIncomeValidate,
} from './fieldRules';
import { PHONE_TYPES, EMAIL_TYPES } from '../../../constants/contactTypes';
import {
  ADDITIONAL_PHONE_TYPE_OPTIONS,
  ADDITIONAL_EMAIL_TYPE_OPTIONS,
  DEFAULT_PHONE_TYPE,
  DEFAULT_EMAIL_TYPE,
  getAdditionalContactEntries,
} from './additionalContactFields';
import useMultiRaceSelect from '../../../hooks/useMultiRaceSelect';
import { buildDemographicFields } from './demographicFields';
import { buildCommercialGeneralFields } from './commercialGeneralFields';
import messages from './messages';
import appMessages from '../../App/messages';
import { renderReadOnlyField } from './readOnlyField';

// Secondary/Tertiary Phone and Email are never required, unlike the
// primary fields getPhoneFieldRules/getEmailFieldRules were built for.
// Neither has a dedicated "format-only, never required" mode, but each
// already has one: isAdult: false returns undefined (no rules at all),
// the same state a non-required field is already in, while a value typed
// in still gets the variant's own built-in format validation.
const ADDITIONAL_PHONE_FIELD_RULES = getPhoneFieldRules({ isAdult: false });
const ADDITIONAL_EMAIL_FIELD_RULES = getEmailFieldRules({ isAdult: false });

const DEFAULT_OPTION = { value: '', text: 'Choose', disabled: true };

const withChooseOption = (options = []) => {
  if (!Array.isArray(options) || options.length === 0) {
    return [DEFAULT_OPTION];
  }

  if (options[0]?.value === '' || options[0]?.value === 'default') {
    return options;
  }

  return [DEFAULT_OPTION, ...options];
};

// MUI's Select blocks click/keyboard selection on a `disabled` MenuItem, so
// DEFAULT_OPTION above (used by withChooseOption) can't be re-selected once
// a real value is chosen — there is no other way to clear these fields back
// to blank. Only safe for conditionally-required selects, since Save must
// already tolerate them blank; only used where withChooseOption otherwise
// would be.
const CLEARABLE_DEFAULT_OPTION = { value: '', text: 'Choose' };

const withClearableChooseOption = (options = []) => {
  if (!Array.isArray(options) || options.length === 0) {
    return [CLEARABLE_DEFAULT_OPTION];
  }

  const [first, ...rest] = options;
  // states/countries (App/selectors.js's getStateOptions/getCountryOptions)
  // already come with their own blank placeholder baked in — still
  // `disabled: true`, so it has the same problem as DEFAULT_OPTION above.
  // Un-disable it in place rather than prepending a second blank option.
  if (first?.value === '' || first?.value === 'default') {
    return [{ ...first, disabled: false }, ...rest];
  }

  return [CLEARABLE_DEFAULT_OPTION, ...options];
};

// Un-disabling "Choose" (above) is what makes it clickable, but it also
// makes MUI render it exactly like a normal option — no more muted/grey
// look a disabled MenuItem gets for free. MUI stamps `data-value` on every
// rendered MenuItem (SelectInput.js), so it can be targeted by its actual
// value (whatever withClearableChooseOption resolved it to — '' normally,
// 'default' for states/countries) via the SingleSelect -> MUI Select's own
// MenuProps, without needing disabled at all.
const clearableSelectProps = (options) => {
  const resolvedOptions = withClearableChooseOption(options);
  const blankValue = resolvedOptions[0]?.value ?? '';
  return {
    options: resolvedOptions,
    MenuProps: {
      sx: {
        [`& .MuiMenuItem-root[data-value="${blankValue}"]`]: {
          color: 'text.disabled',
        },
      },
    },
  };
};

// A hidden row (via `hidden`, not array omission -- that's what lets the
// Form library auto-clear its RHF value on hide) still contributes its
// Grid rowSpacing as vertical padding even at 0 height: MUI's Collapse
// only zeroes `height`, never width or Grid v2's own padding-based
// spacing. Zeroing this row's own padding removes that stacked
// whitespace, without touching its reserved width (still needed for
// row-parity, see below).
const ADDITIONAL_ROW_HIDDEN_SPACING = { py: 0 };

// Pre-flag: the Required / Conditionally Required General fields. End Date is
// N/A too but gets its own certLock block through its disabled chain below, so
// it isn't named here. Every list in this block is module level so the sections
// memo is never churned by a fresh array per render.
const CERT_LOCKED_GENERAL_FIELDS_MINOR = [
  'generalInformation.firstName',
  'generalInformation.lastName',
  'generalInformation.relationshipId',
  'generalInformation.startDate',
];
// Type, Phone and Email Address are Required for an ADULT only -- a minor has
// no Type field at all, and Phone/Email Address are omitted entirely for a
// minor (no Customer record/contact columns to save one to), so neither
// belongs on the minor list above.
const CERT_LOCKED_GENERAL_FIELDS_ADULT = [
  ...CERT_LOCKED_GENERAL_FIELDS_MINOR,
  'generalInformation.applicantTypeId',
  'generalInformation.phoneNumber',
  'generalInformation.emailAddress',
  // Phone Type/Email Type are N/A in the field list, not Required -- locked
  // here anyway. Freezing a phone number while its type stays pickable reads
  // as broken, not as a real permission, so these two are locked as a set
  // with the value beside them rather than following the field list.
  'generalInformation.phoneType',
  'generalInformation.emailType',
];

// Post-flag. The legacy per-member modal this screen replaces
// (EditOccupantModal) gated nothing on an open certification, so the lock keeps
// only the household composition a cert actually freezes.
const CERT_LOCKED_GENERAL_FIELDS_CONVENTIONAL = [
  'generalInformation.startDate',
];
// Conventional has no certified composition to protect.
const CERT_LOCKED_GENERAL_FIELDS_AFFORDABLE = [
  ...CERT_LOCKED_GENERAL_FIELDS_CONVENTIONAL,
  'generalInformation.applicantTypeId',
  'generalInformation.relationshipId',
];
// Never editable in the legacy modal either (hard-disabled there).
const CERT_LOCKED_DEMOGRAPHIC_FIELDS_AFFORDABLE = [
  'affordableInformation.affordableRelationshipId',
];
// Empty blocks nothing; absent means the whole section.
const CERT_LOCKED_NO_FIELDS = [];

// Selection only -- a fresh array here would churn the sections memo.
const certLockedGeneralFieldsFor = ({
  isNarrowedToComposition,
  isAffordable,
  isAdult,
}) => {
  if (!isNarrowedToComposition) {
    return isAdult
      ? CERT_LOCKED_GENERAL_FIELDS_ADULT
      : CERT_LOCKED_GENERAL_FIELDS_MINOR;
  }
  return isAffordable
    ? CERT_LOCKED_GENERAL_FIELDS_AFFORDABLE
    : CERT_LOCKED_GENERAL_FIELDS_CONVENTIONAL;
};

// Conventional Demographics is only Special Needs and Annual Income. A minor's
// Affordable Relation stays editable unless an open cert is in a locked
// compliance status.
const certLockedDemographicFieldsFor = ({
  isNarrowedToComposition,
  isAffordable,
  isAdult,
  hasLockedOpenCert,
  lockInfoPending,
}) => {
  if (!isNarrowedToComposition) return undefined;
  if (!isAffordable) return CERT_LOCKED_NO_FIELDS;
  return isAdult || hasLockedOpenCert || lockInfoPending
    ? CERT_LOCKED_DEMOGRAPHIC_FIELDS_AFFORDABLE
    : CERT_LOCKED_NO_FIELDS;
};

// What the Type cascade below writes to clear Relation / Affordable
// Relation. Not '': 'default' is the value BOTH selects' own blank "Choose"
// option carries (App/selectors.js's getRelationshipOptions /
// getAffordableRelationshipOptions, shared with the legacy form), so the
// field renders as "Choose" rather than blank -- and it is the only value
// buildSavePayload turns into an explicit null for relationshipId
// (withoutDefaultRelationshipId). '' is swallowed by blankToUndefined there,
// which omits the key and silently leaves the previously-saved Relation in
// the database. Affordable Relation's nullIfBlank accepts either.
const CASCADE_CLEARED_VALUE = 'default';

// Value keeps the same md:6 as the primary Phone/Email field in every
// reveal state, unlike Type/the trash slot below.
const additionalValueGridProps = (isRowHidden) => ({
  xs: 12,
  md: 6,
  ...(isRowHidden ? { sx: ADDITIONAL_ROW_HIDDEN_SPACING } : {}),
});

// Only the row currently showing the trash icon narrows Type to make room
// for it; otherwise Type matches the primary field's md:6, same as Value.
const additionalTypeGridProps = (isRowHidden, showsTrash) => ({
  xs: 12,
  md: showsTrash ? 5 : 6,
  ...(isRowHidden ? { sx: ADDITIONAL_ROW_HIDDEN_SPACING } : {}),
});

// Only reserves its md:1 share on the row currently showing the icon,
// collapsing to md:0 otherwise so Type above can reclaim the full md:6.
//
// `p: 0` on the md:0 case also zeros its own columnSpacing padding --
// MUI Grid v2 applies spacing as real CSS padding, not a gap
// (generateGridStyles in @mui/system/Unstable_Grid/gridGenerator.js), and
// with box-sizing: border-box a "0 width" item still renders at its
// padding's width. Left un-zeroed, once Value+Type already fill a row
// (md:6 + md:6 = 12), that leftover sliver overflows it and wraps the
// icon onto its own line.
//
// `height: '40px'` pins this cell to the input's own height (rather than
// stretching to the row's tallest sibling, which also includes the
// field's label and helper-text line) so `alignItems: 'center'` centers
// against the input itself. `mt` clears the label above it -- tuned
// visually, since MUI's own theme-declared label height doesn't quite
// land it in practice.
const additionalTrashGridProps = (showsTrash) => ({
  xs: 12,
  md: showsTrash ? 1 : 0,
  sx: showsTrash
    ? { display: 'flex', alignItems: 'center', height: '40px', mt: '32px' }
    : { p: 0 },
});

// Phone Number and Email Address never had an accompanying Type field.
// occupantInfo.contactInformation already carries a primary entry with a
// type; this reads it for the first time.
const getPrimaryContactEntry = (entries = []) =>
  (entries ?? []).find((entry) => entry?.isPrimary);

const getDefaultValues = (
  occupantInfo = {},
  {
    annualIncome,
    isEditHouseholdMemberDatesEnabled = false,
    isCommercial = false,
    showMultiRace = false,
  } = {},
) => {
  const residentInfo = occupantInfo?.residentInfo ?? {};
  const generalInformation = residentInfo?.generalInformation ?? {};
  const personalInformation = residentInfo?.personalInformation ?? {};
  const affordableInformation = residentInfo?.affordableInformation ?? {};
  const primaryPhoneEntry = getPrimaryContactEntry(
    occupantInfo?.contactInformation?.phones,
  );
  const primaryEmailEntry = getPrimaryContactEntry(
    occupantInfo?.contactInformation?.emails,
  );

  // Populated from the loaded record's non-primary ContactPhone/
  // ContactEmail rows only while the flag is on. Gated here too, not just
  // at render/save time -- defaultValues seed react-hook-form's internal
  // state even for a field that never renders, and the flag-off form must
  // stay byte-for-byte the same as before.
  const contactInformation = isEditHouseholdMemberDatesEnabled
    ? occupantInfo?.contactInformation ?? {}
    : {};
  const [secondaryPhoneEntry, tertiaryPhoneEntry] = getAdditionalContactEntries(
    contactInformation.phones,
  );
  const [secondaryEmailEntry, tertiaryEmailEntry] = getAdditionalContactEntries(
    contactInformation.emails,
  );

  return {
    generalInformation: {
      // Unlike the other fields below, occupantInfo.preferredName isn't a
      // reliable raw value to fall back to — useHouseholdMembersQuery.
      // helpers.js defaults it to the literal string '---' for table
      // display when blank, which would otherwise leak into this editable
      // field as a real (savable!) value.
      preferredName: generalInformation.preferredName ?? '',
      firstName: generalInformation.firstName ?? occupantInfo?.firstName ?? '',
      middleName:
        generalInformation.middleName ?? occupantInfo?.middleName ?? '',
      lastName: generalInformation.lastName ?? occupantInfo?.lastName ?? '',
      suffixId: generalInformation.suffixId ?? '',
      phoneNumber:
        generalInformation.phoneNumber ?? occupantInfo?.phoneNumber ?? '',
      phoneType:
        generalInformation.phoneType ??
        primaryPhoneEntry?.phoneType ??
        DEFAULT_PHONE_TYPE,
      applicantTypeId:
        generalInformation.applicantTypeId ??
        occupantInfo?.applicantTypeId ??
        '',
      relationshipId:
        generalInformation.relationshipId ?? occupantInfo?.relationshipId ?? '',
      emailAddress:
        generalInformation.emailAddress ?? occupantInfo?.emailAddress ?? '',
      emailType:
        generalInformation.emailType ??
        primaryEmailEntry?.emailType ??
        DEFAULT_EMAIL_TYPE,
      startDate: generalInformation.startDate ?? occupantInfo?.startDate ?? '',
      endDate: generalInformation.endDate ?? occupantInfo?.endDate ?? '',
      // Gated so residential defaultValues stay unchanged. Read from
      // residentInfo only: occupantInfo.parentCompany is the table's "---"
      // display placeholder, same trap as preferredName above.
      ...(isCommercial
        ? {
            parentCompany: generalInformation.parentCompany ?? '',
            street: generalInformation.street ?? '',
            street2: generalInformation.street2 ?? '',
            city: generalInformation.city ?? '',
            state: generalInformation.state ?? '',
            zipCode: generalInformation.zipCode ?? '',
          }
        : {}),
      // ids are deliberately not tracked here: CustomerContactProcessor.
      // processPhones/processEmails always fully deletes and recreates
      // every row on save regardless of what id (if any) is sent, so
      // round-tripping one back has no effect -- these are 2 fixed slots,
      // not a dynamic list needing React keys.
      // Type defaults to Mobile/Personal the moment a slot is revealed via
      // Add, matching the application form's own Contact Information
      // section -- only when there's no existing entry to show instead, so
      // a saved 'work'/'home' phone still shows its real type on load.
      secondaryPhone: {
        phoneNumber: secondaryPhoneEntry?.phoneNumber ?? '',
        phoneType: secondaryPhoneEntry?.phoneType ?? DEFAULT_PHONE_TYPE,
      },
      tertiaryPhone: {
        phoneNumber: tertiaryPhoneEntry?.phoneNumber ?? '',
        phoneType: tertiaryPhoneEntry?.phoneType ?? DEFAULT_PHONE_TYPE,
      },
      secondaryEmail: {
        emailAddress: secondaryEmailEntry?.emailAddress ?? '',
        emailType: secondaryEmailEntry?.emailType ?? DEFAULT_EMAIL_TYPE,
      },
      tertiaryEmail: {
        emailAddress: tertiaryEmailEntry?.emailAddress ?? '',
        emailType: tertiaryEmailEntry?.emailType ?? DEFAULT_EMAIL_TYPE,
      },
    },
    personalInformation: {
      maritalStatus: personalInformation.maritalStatus ?? '',
      birthday: personalInformation.birthday ?? '',
      // Applicant.annualIncome — not part of residentInfo (which only ever
      // holds what this Mini-Profile itself saved before); sourced from a
      // separate resident-detail fetch (see MiniProfileBridge/index.js) since
      // useHouseholdMembersQuery's list response doesn't carry it either.
      annualIncome: personalInformation.annualIncome ?? annualIncome ?? '',
      stateIdNumber: personalInformation.stateIdNumber ?? '',
      stateIdOrigin: personalInformation.stateIdOrigin ?? '',
      socialSecurityNumber: personalInformation.socialSecurityNumber ?? '',
      passportNumber: personalInformation.passportNumber ?? '',
      passportCountry: personalInformation.passportCountry ?? '',
      alienRegistration:
        personalInformation.alienRegistration ??
        personalInformation.alienRegistrationNumber ??
        '',
      dateOfBirth: personalInformation.dateOfBirth ?? '',
      alienRegistrationNumber:
        personalInformation.alienRegistrationNumber ?? '',
    },
    affordableInformation: {
      gender: affordableInformation.gender ?? '',
      veteran: affordableInformation.veteran ?? '',
      jointCustody: affordableInformation.jointCustody ?? '',
      race: affordableInformation.race ?? '',
      // Registered only where multi-race is shown: a save without it leaves
      // the stored selections to the backend, which clears them so reports
      // fall back to the single Race the user can see.
      ...(showMultiRace
        ? { multiRace: affordableInformation.multiRace ?? [] }
        : {}),
      ethnicity: affordableInformation.ethnicity ?? '',
      affordableRelationshipId:
        affordableInformation.affordableRelationshipId ??
        occupantInfo?.affordableRelationshipId ??
        '',
      studentType: affordableInformation.studentType ?? '',
      specialNeedsDesignationIds:
        affordableInformation.specialNeedsDesignationIds ?? [],
      disability: {
        hasDisability: affordableInformation?.disability?.hasDisability ?? '',
        disabilityMobile:
          affordableInformation?.disability?.disabilityMobile ?? '',
        disabilityHearing:
          affordableInformation?.disability?.disabilityHearing ?? '',
        disabilityVisual:
          affordableInformation?.disability?.disabilityVisual ?? '',
        disabilityOther:
          affordableInformation?.disability?.disabilityOther ?? '',
      },
    },
  };
};

export const useMiniProfileSections = ({
  occupantInfo,
  suffixes = [],
  relationshipOptions = [],
  // Child-flagged relationships only (App/selectors.js#
  // getMinorRelationshipOptions). Relation reads toward the head of
  // household, so a minor can't hold Parent/Partner/Guarantor -- ENG-31011.
  minorRelationshipOptions = [],
  applicantTypeOptions = [],
  agedUpMinorApplicantTypeId = null,
  states = [],
  countries = [],
  affordableRelationships = [],
  specialNeedsDesignations = [],
  isAffordable = false,
  // Commercial tenant: General swaps to the business-shaped field list and
  // Personal/Demographics are dropped -- see commercialGeneralFields.js.
  isCommercial = false,
  isPending = false,
  // True while creating a brand-new household member (no occupantInfo.id
  // yet). Computed by the caller and passed in, like isPending -- not
  // derived internally, so every existing caller/test that never passes
  // it keeps its prior (editing) behavior.
  isAdding = false,
  // True only for the applicant-stage subject (MiniProfileBridge's
  // `subject.kind === 'applicant'`) -- not a repurposing of isPending/
  // isAdding, neither of which means "applicant subject". Scopes the
  // Affordable Relation exception below to exactly "adding an adult through
  // the applicant path" -- the resident Add flow must keep deferring this
  // field to the application, untouched.
  isApplicantSubject = false,
  householdHasOpenCerts = false,
  // Any open cert in a locked compliance status (Approved / Approved Pending
  // Signatures / Denied). Only gates a minor's Affordable Relation.
  hasLockedOpenCert = false,
  // True while the lock info has not resolved (request in flight or failed).
  // Fails closed: an unconfirmed lock keeps the minor's Affordable Relation
  // disabled rather than briefly exposing a field an approved cert forbids.
  lockInfoPending = false,
  // The household itself moved out (ResidentProfile's isPriorResident
  // selector), not the member's own 'Prior' status -- a Prior member inside a
  // Current household is a normal case and stays cert-locked.
  isPriorResident = false,
  // Owned by the caller: this hook takes no permission dependencies.
  canEditStartEndDates = true,
  householdMoveInDate = null,
  householdMoveOutDate = null,
  annualIncome = null,
  // Live personalInformation values, watched off the actual RHF form by
  // MiniProfileBridge/index.js — null until the form exists / the user
  // hasn't touched any identification field yet, in which case the
  // identification icon/helper text below falls back to defaultValues.
  livePersonalInformation = null,
  // Live affordableInformation.disability.hasDisability, watched the same
  // way — null until the user actually changes it, in which case the
  // sub-fields' disabled state below falls back to defaultValues.
  liveHasDisability = null,
  // Live affordableInformation values, watched the same way — used to
  // detect a previously-saved value being cleared back out (see
  // hasClearedRequiredField below).
  liveAffordableInformation = null,
  // Live generalInformation.applicantTypeId, watched the same way. The Type
  // cascade below has to react to the type the user just picked, and
  // defaultValues never changes while the modal is open — reading it from
  // there would set the two paired fields but leave them editable until the
  // next reopen.
  liveApplicantTypeId = null,
  // The two applicant types that force a fixed Relation + Affordable
  // Relation, shaped { applicantType, relationship, affordableRelationship }
  // (App/selectors.js's getGuarantorOptions/getLiveInCaretakerOptions), the
  // same objects the legacy Edit Household form cascades from. null leaves
  // the cascade out entirely, since there is then no id to match a pick
  // against.
  guarantorOptions = null,
  liveInCaretakerOptions = null,
  // Gates the Secondary/Tertiary Phone/Email fields behind the epic-wide
  // `edit-household-member-dates` flag. Defaults to false so every
  // existing caller/test that never passes it keeps rendering exactly as
  // before.
  isEditHouseholdMemberDatesEnabled = false,
  // Secondary/Tertiary Phone and Email start hidden and are revealed one
  // at a time via an "Add" link, matching the legacy EditResidentHousehold
  // ContactFieldArray's Primary/Secondary/Tertiary pattern, rather than
  // shown as two permanently-empty fields. null means "no explicit action
  // yet this session" and falls back to however many entries the loaded
  // record already has, like the live*-null-fallback props below. Owned
  // by the caller (not local state) so it can be reset on close/reopen.
  revealedAdditionalPhoneCount = null,
  setRevealedAdditionalPhoneCount,
  revealedAdditionalEmailCount = null,
  setRevealedAdditionalEmailCount,
  // Explicitly blanks a Secondary/Tertiary slot's own react-hook-form
  // value at the moment its trash icon is clicked -- the Collapse+`hidden`
  // wrapper around a removed field only animates it out of view, it does
  // not clear the value underneath, so without this a Remove followed by
  // an Add in the same session showed the old value right back.
  clearAdditionalPhoneSlot,
  clearAdditionalEmailSlot,
  // Task 4's hook return ({ options, selectedAction, onActionChange,
  // banners }). Omitted entirely for callers that haven't wired it up yet
  // (or for a member type/status where it doesn't apply), in which case
  // Account Management is left out rather than rendered empty.
  portalAccountActions = null,
  // Applicant path only: no lastSystemAccess on the record, so these come from the status endpoint.
  portalAccess = null,
}) => {
  const intl = useIntl();
  const formatMessage = intl.formatMessage;
  const {
    showMultiSelect,
    stateCode: propertyStateCode,
    affordablePrograms: propertyAffordablePrograms,
  } = useMultiRaceSelect();
  const showMultiRace = isAffordable && showMultiSelect;
  const multiRaceOptions = useMemo(
    () =>
      raceOptions(propertyStateCode, {
        withDefault: false,
        affordablePrograms: propertyAffordablePrograms,
      }),
    [propertyStateCode, propertyAffordablePrograms],
  );
  const isAdult = occupantInfo?.type === 'adult';
  const isPrimary = Boolean(occupantInfo?.isPrimary);
  const referralType = occupantInfo?.referralType ?? '---';
  // Narrow scalars rather than depending on all of occupantInfo below --
  // the sections memo only ever needs these five strings out of it.
  const resolvedPortalAccess =
    portalAccess ?? occupantInfo?.residentInfo?.portalAccess;
  const portalEmailAddress = resolvedPortalAccess?.emailAddress;
  const portalClaimed = resolvedPortalAccess?.claimed;
  const portalLastLogin = resolvedPortalAccess?.lastLogin;
  const portalInviteSent = resolvedPortalAccess?.inviteSent;
  const occupantStatus = occupantInfo?.status;
  // Same narrowing, same reason, but for portalAccountActions:
  // usePortalAccountActions returns a fresh object literal every render
  // (never memoized), so depending on the object itself would make this memo
  // recompute -- and rebuild every field/element in every section, not just
  // portal's -- on every render of the consumer. These four leaves hold
  // identity across renders only because that hook keeps them stable
  // (memoized array/banners, useCallback over a handler ref); this narrowing
  // is worthless the moment one of them starts churning, so the stability
  // test there guards this memo as much as it guards that hook.
  const portalActionOptions = portalAccountActions?.options;
  const portalActionBanners = portalAccountActions?.banners;
  const portalActionSelectedAction = portalAccountActions?.selectedAction;
  const portalActionOnChange = portalAccountActions?.onActionChange;
  // Same contactInformation/getAdditionalContactEntries call
  // getDefaultValues makes to populate secondaryPhone/tertiaryPhone/
  // secondaryEmail/tertiaryEmail, recomputed here since getDefaultValues is
  // a plain module-level function outside this hook's closure -- `hidden`
  // below must never disagree with what defaultValues actually populated,
  // so this derives from the identical inputs via the identical utility.
  const savedAdditionalPhoneEntries = getAdditionalContactEntries(
    isEditHouseholdMemberDatesEnabled
      ? occupantInfo?.contactInformation?.phones
      : undefined,
  );
  const savedAdditionalEmailEntries = getAdditionalContactEntries(
    isEditHouseholdMemberDatesEnabled
      ? occupantInfo?.contactInformation?.emails
      : undefined,
  );
  // null (nothing clicked yet this session) falls back to what's already
  // saved, so an existing member's Secondary/Tertiary show revealed on open;
  // once the user clicks Add/Remove, the explicit count takes over until the
  // next close/reopen resets it back to null (MiniProfileBridge/index.js).
  const revealedPhoneCount =
    revealedAdditionalPhoneCount ?? savedAdditionalPhoneEntries.length;
  const revealedEmailCount =
    revealedAdditionalEmailCount ?? savedAdditionalEmailEntries.length;
  const isSecondaryPhoneRowHidden = revealedPhoneCount < 1;
  const isTertiaryPhoneRowHidden = revealedPhoneCount < 2;
  const isSecondaryEmailRowHidden = revealedEmailCount < 1;
  const isTertiaryEmailRowHidden = revealedEmailCount < 2;
  // The trash icon (and the narrower Type it makes room for) only ever
  // belongs to whichever slot is currently last. Secondary is "last" only
  // until Tertiary is revealed too; Tertiary, once visible, always is
  // (the cap is 2).
  const secondaryPhoneShowsTrash = revealedPhoneCount === 1;
  const tertiaryPhoneShowsTrash = revealedPhoneCount === 2;
  const secondaryEmailShowsTrash = revealedEmailCount === 1;
  const tertiaryEmailShowsTrash = revealedEmailCount === 2;
  const defaultValues = useMemo(
    () =>
      getDefaultValues(occupantInfo, {
        annualIncome,
        isEditHouseholdMemberDatesEnabled,
        isCommercial,
        showMultiRace,
      }),
    [
      occupantInfo,
      annualIncome,
      isEditHouseholdMemberDatesEnabled,
      isCommercial,
      showMultiRace,
    ],
  );
  // There is no Resident/lease-addendum row yet to hang a Start/End Date
  // off of while creating a member (the create payload has no
  // startDate/endDate field at all) -- same treatment as Pending, which
  // has the identical "no record to date yet" problem.
  const datesDisabled = isPending || isAdding;
  // A new certification cannot be started from the application, and the legacy
  // application page never gated on open certs -- so the lock is per page. A
  // Prior household is exempt too: its open cert may be a historical one
  // started precisely to correct the data this would freeze. A member being
  // created is exempt for the same underlying reason: they are not part of
  // the open certification, so there is nothing about them to freeze --
  // without this, Add inherits the household's lock and Save (which has no
  // gate of its own for this) ships blank required fields to a create.
  const certLock =
    householdHasOpenCerts &&
    !isApplicantSubject &&
    !isPriorResident &&
    !isAdding;
  // certLock sits ahead of every other reason either date field can be
  // disabled -- it composes with (never replaces) the checks below, on both
  // fields, regardless of Required/N/A status in the field list.
  const startDateDisabled = datesDisabled || certLock || !canEditStartEndDates;
  // Start/End Date each get their own exact Pending helper text (not a
  // single shared "Start Date and End Date..." string). certLock leads the
  // chain since it is the household-level rule, checked ahead of every
  // page/field-level reason below it.
  const startDateHelperText = certLock
    ? formatMessage(messages.openCertHelperText)
    : isAdding
    ? formatMessage(
        isAdult
          ? messages.addingAdultStartDateHelperText
          : messages.addingMinorStartDateHelperText,
      )
    : isPending
    ? formatMessage(messages.pendingStartDateHelperText)
    : !canEditStartEndDates
    ? formatMessage(messages.noStartDatePermission)
    : undefined;
  // certLock is checked first, same as Start Date; End Date is additionally
  // blocked for the current Head of Household on top of it -- a household
  // must always have exactly one HoH.
  const endDateDisabled =
    datesDisabled || certLock || isPrimary || !canEditStartEndDates;
  // What getEndDateValidate is told, which mirrors endDateDisabled except for
  // isPrimary: its own Head of Household branch sits behind that flag
  // instead. Everything else here (certLock included) is a reason the user
  // cannot touch the field at all, so a bad legacy value must not block
  // Save forever.
  // NOTE: endDateDisabled always includes isPrimary, and the component
  // library strips `validate` from a disabled field's rules entirely -- so
  // the Head of Household branch this unlocks can never actually run through
  // the rendered form. It's inert there, exercised only by unit tests
  // calling rules.validate directly. Left in place (not collapsed into
  // endDateDisabled) as a real second guard, in case that stripping behavior
  // or endDateDisabled's own composition ever changes.
  const endDateValidateDisabled =
    datesDisabled || certLock || !canEditStartEndDates;
  // Narrowed to the scalar rather than depending on all of defaultValues, so
  // the endDateValidate memo below doesn't churn on unrelated field changes.
  const loadedEndDate = defaultValues.generalInformation.endDate;
  const endDateHelperText = certLock
    ? formatMessage(messages.openCertHelperText)
    : isAdding
    ? formatMessage(
        isAdult
          ? messages.addingAdultEndDateHelperText
          : messages.addingMinorEndDateHelperText,
      )
    : isPending
    ? formatMessage(messages.pendingEndDateHelperText)
    : !canEditStartEndDates
    ? formatMessage(messages.noEndDatePermission)
    : isPrimary
    ? formatMessage(messages.headOfHouseholdEndDateHelperText)
    : undefined;
  const startDateValidate = useMemo(
    () =>
      getStartDateValidate({
        disabled: startDateDisabled,
        householdMoveInDate,
        householdMoveOutDate,
        isPriorResident,
        formatMessage,
      }),
    [
      startDateDisabled,
      householdMoveInDate,
      householdMoveOutDate,
      isPriorResident,
      formatMessage,
    ],
  );
  const endDateValidate = useMemo(
    () =>
      getEndDateValidate({
        disabled: endDateValidateDisabled,
        householdMoveInDate,
        householdMoveOutDate,
        isPriorResident,
        isPrimary,
        endDate: loadedEndDate,
        formatMessage,
      }),
    [
      endDateValidateDisabled,
      householdMoveInDate,
      householdMoveOutDate,
      isPriorResident,
      isPrimary,
      loadedEndDate,
      formatMessage,
    ],
  );
  const annualIncomeValidate = useMemo(
    () => getAnnualIncomeValidate({ formatMessage }),
    [formatMessage],
  );
  const dateOfBirthValidate = useMemo(
    () => getDateOfBirthValidate({ formatMessage }),
    [formatMessage],
  );

  // This needs to update live as the user types/selects, not just after
  // Save+reopen — livePersonalInformation (watched off the real form by
  // MiniProfileBridge/index.js) wins once it exists; defaultValues only
  // covers the very first render, before anything's been watched yet.
  const identificationPersonalInformation = useMemo(() => {
    const source = livePersonalInformation ?? defaultValues.personalInformation;
    // stateIdOrigin/passportCountry's own options (App/selectors.js's
    // getStateOptions/getCountryOptions) use 'default' as their blank/
    // "Choose" placeholder value, not '' — getIdentificationIconState's
    // hasIdentificationValue only treats null/undefined/'' as empty, so
    // selecting "Choose" to clear one back out left it looking answered and
    // the icon never came back.
    return {
      ...source,
      stateIdOrigin:
        source.stateIdOrigin === 'default' ? '' : source.stateIdOrigin,
      passportCountry:
        source.passportCountry === 'default' ? '' : source.passportCountry,
    };
  }, [livePersonalInformation, defaultValues]);
  // See fieldRules.js for the reasoning behind each of these — the
  // identification icon and the pre-existing-data relaxations for
  // SSN/Email/Phone/Type/Relationship found while browser-testing Save.
  const {
    fieldsShowingIcon,
    identificationHelperText,
    hasAtLeastOneIdentification,
  } = useMemo(
    () =>
      getIdentificationIconState({
        personalInformation: identificationPersonalInformation,
        isAdult,
        isAffordable,
      }),
    [identificationPersonalInformation, isAdult, isAffordable],
  );
  // The helper text is the instruction for Save being blocked, so it goes
  // away once a complete identification exists.
  // Per-field, not a single shared flag — each identification field only
  // keeps its icon/helper text while its own group (state ID, SSN, passport,
  // alien registration) is the one the user is actually filling in.
  // Whether the record as loaded already had a usable identification, as
  // opposed to whatever is in the form right now. A minor is only held to the
  // identification rule once one has been provided — it must not block saving
  // other information the user does have (see MiniProfileBridge/index.js).
  const hadIdentificationOnLoad = useMemo(
    () =>
      getIdentificationIconState({
        personalInformation: defaultValues.personalInformation,
        isAdult,
        isAffordable,
      }).hasAtLeastOneIdentification,
    [defaultValues, isAdult, isAffordable],
  );
  const withIdentificationIcon = useMemo(
    () => (fieldKey) => (label) =>
      fieldsShowingIcon[fieldKey]
        ? withConditionallyRequiredIcon(label)
        : label,
    [fieldsShowingIcon],
  );
  const identificationHelperTextFor = useMemo(
    () => (fieldKey) =>
      fieldsShowingIcon[fieldKey] && !hasAtLeastOneIdentification
        ? identificationHelperText
        : undefined,
    [fieldsShowingIcon, identificationHelperText, hasAtLeastOneIdentification],
  );

  // Every field carrying the generic conditionally-required icon (Date of
  // Birth, Marital Status, Gender, Veteran/Joint Custody, Disabled + its 4
  // sub-fields, Race, Ethnicity, Affordable Relation, Student, Special
  // Needs Designation) is never hard-required — see the fields below — but
  // once a value has actually been saved for one, clearing it back out
  // (deleting the date, re-picking "Choose" on a select, or unchecking
  // every Special Needs option) must block Save. Unlike the identification
  // group above, none of these are substitutes for each other, so this is
  // a per-field check, not a "is the whole group empty" one.
  const currentAffordableInformation = useMemo(() => {
    const source =
      liveAffordableInformation ?? defaultValues.affordableInformation;
    // affordableRelationshipId's own options (App/selectors.js's
    // getAffordableRelationshipOptions) use 'default' as their blank/
    // "Choose" placeholder value, not '' like every other select here —
    // same quirk identificationPersonalInformation already works around
    // for stateIdOrigin/passportCountry. Left un-normalized, re-selecting
    // "Choose" looked like a real (non-empty) value below.
    return {
      ...source,
      affordableRelationshipId:
        source.affordableRelationshipId === 'default'
          ? ''
          : source.affordableRelationshipId,
    };
  }, [liveAffordableInformation, defaultValues]);
  const wasFieldCleared = (savedValue, currentValue) =>
    Boolean(savedValue) && !currentValue;
  // Boolean([]) is true — an empty array is still a truthy object — so the
  // single-value check above can't tell "had items" from "always empty".
  const wasArrayFieldCleared = (savedValue, currentValue) =>
    (savedValue?.length ?? 0) > 0 && (currentValue?.length ?? 0) === 0;
  // Named rather than inlined into the list below: the Affordable Relation
  // field reads it too, to say why Save went dead. Blanking a saved value
  // here blocks Save whether the user re-picked "Choose" or the Type cascade
  // cleared it for them, and nothing else on screen explains that.
  const wasAffordableRelationshipCleared = wasFieldCleared(
    // parseAffordableInfo (useHouseholdMembersQuery.helpers.js) defaults
    // this to the string 'default' when unset, same placeholder as the
    // field's own options — without normalizing here too, every member
    // who never set one would already look "cleared" from first render.
    defaultValues.affordableInformation.affordableRelationshipId === 'default'
      ? ''
      : defaultValues.affordableInformation.affordableRelationshipId,
    currentAffordableInformation.affordableRelationshipId,
  );
  // Blanked by rule on that answer, not by the user -- counting them would
  // leave Save dead for anyone who had answered before opting out.
  const declinedDisability =
    currentAffordableInformation.disability?.hasDisability ===
    'notWishToProvide';
  const wasDisabilitySubFieldCleared = (savedValue, currentValue) =>
    !declinedDisability && wasFieldCleared(savedValue, currentValue);
  const hasClearedRequiredField = [
    isAdult &&
      wasFieldCleared(
        defaultValues.personalInformation.maritalStatus,
        identificationPersonalInformation.maritalStatus,
      ),
    isAdult &&
      wasFieldCleared(
        defaultValues.personalInformation.birthday,
        identificationPersonalInformation.birthday,
      ),
    wasFieldCleared(
      defaultValues.affordableInformation.gender,
      currentAffordableInformation.gender,
    ),
    isAdult &&
      wasFieldCleared(
        defaultValues.affordableInformation.veteran,
        currentAffordableInformation.veteran,
      ),
    !isAdult &&
      wasFieldCleared(
        defaultValues.affordableInformation.jointCustody,
        currentAffordableInformation.jointCustody,
      ),
    !isAdult &&
      wasFieldCleared(
        defaultValues.affordableInformation.studentType,
        currentAffordableInformation.studentType,
      ),
    wasFieldCleared(
      defaultValues.affordableInformation.disability.hasDisability,
      currentAffordableInformation.disability?.hasDisability,
    ),
    wasDisabilitySubFieldCleared(
      defaultValues.affordableInformation.disability.disabilityMobile,
      currentAffordableInformation.disability?.disabilityMobile,
    ),
    wasDisabilitySubFieldCleared(
      defaultValues.affordableInformation.disability.disabilityHearing,
      currentAffordableInformation.disability?.disabilityHearing,
    ),
    wasDisabilitySubFieldCleared(
      defaultValues.affordableInformation.disability.disabilityVisual,
      currentAffordableInformation.disability?.disabilityVisual,
    ),
    wasDisabilitySubFieldCleared(
      defaultValues.affordableInformation.disability.disabilityOther,
      currentAffordableInformation.disability?.disabilityOther,
    ),
    wasFieldCleared(
      defaultValues.affordableInformation.race,
      currentAffordableInformation.race,
    ),
    // Race itself isn't editable while the multi-select is shown, so its
    // check above never fires there; clearing every selection is what
    // wipes it on save.
    showMultiRace &&
      wasArrayFieldCleared(
        defaultValues.affordableInformation.multiRace,
        currentAffordableInformation.multiRace,
      ),
    wasFieldCleared(
      defaultValues.affordableInformation.ethnicity,
      currentAffordableInformation.ethnicity,
    ),
    wasAffordableRelationshipCleared,
    wasArrayFieldCleared(
      defaultValues.affordableInformation.specialNeedsDesignationIds,
      currentAffordableInformation.specialNeedsDesignationIds,
    ),
  ].some(Boolean);

  // Mobile/Hearing/Visual/Other Disability are only editable while
  // "Disabled" is "yes" — MiniProfileBridge/index.js forces them back to
  // "no" the moment "Disabled" flips to "no" (matching the same dependency
  // the legacy EditOccupantModal and the Affordable Application already
  // enforce), and disabling them here is what stops the user from
  // re-answering one right as that happens.
  const hasDisability =
    liveHasDisability ??
    defaultValues.affordableInformation.disability.hasDisability;
  const disabilitySubFieldsDisabled = hasDisability !== 'yes';

  // Narrowed to ids rather than depending on the two option objects, same
  // reason as the portal leaves above: the sections memo rebuilds every
  // field in every section when a dependency churns.
  const guarantorApplicantTypeId = guarantorOptions?.applicantType?.id ?? null;
  const guarantorRelationshipId = guarantorOptions?.relationship?.id ?? null;
  const guarantorAffordableRelationshipId =
    guarantorOptions?.affordableRelationship?.id ?? null;
  const liveInCaretakerApplicantTypeId =
    liveInCaretakerOptions?.applicantType?.id ?? null;
  const liveInCaretakerRelationshipId =
    liveInCaretakerOptions?.relationship?.id ?? null;
  const liveInCaretakerAffordableRelationshipId =
    liveInCaretakerOptions?.affordableRelationship?.id ?? null;
  // Falls back to the loaded type until the user actually touches Type, same
  // convention as livePersonalInformation/liveHasDisability above -- a member
  // already saved as a Guarantor must open with both paired fields locked.
  const selectedApplicantTypeId =
    liveApplicantTypeId ?? defaultValues.generalInformation.applicantTypeId;
  const isCascadedApplicantType =
    isEditHouseholdMemberDatesEnabled &&
    [guarantorApplicantTypeId, liveInCaretakerApplicantTypeId]
      .filter(Boolean)
      .includes(selectedApplicantTypeId);
  // Live-aware and already 'default'-normalized (see above), which is what
  // lets the Guarantor option re-enable the moment the user moves off the
  // Head of Household affordable relation.
  const currentAffordableRelationshipId =
    currentAffordableInformation.affordableRelationshipId;

  const ssnFieldRules = useMemo(
    () =>
      getSsnFieldRules({
        ssn: defaultValues.personalInformation.socialSecurityNumber,
        formatMessage,
      }),
    [defaultValues, formatMessage],
  );
  const identificationPairRules = useMemo(
    () => ({
      stateIdNumber: getIdentificationPairRules({
        partnerName: 'personalInformation.stateIdOrigin',
        message: formatMessage(messages.stateIdNumberMissing),
      }),
      stateIdOrigin: getIdentificationPairRules({
        partnerName: 'personalInformation.stateIdNumber',
        message: formatMessage(messages.stateIdOriginMissing),
      }),
      passportNumber: getIdentificationPairRules({
        partnerName: 'personalInformation.passportCountry',
        message: formatMessage(messages.passportNumberMissing),
      }),
      passportCountry: getIdentificationPairRules({
        partnerName: 'personalInformation.passportNumber',
        message: formatMessage(messages.passportCountryMissing),
      }),
    }),
    [formatMessage],
  );
  const emailFieldRules = useMemo(
    () =>
      getEmailFieldRules({
        isAdult,
        email: defaultValues.generalInformation.emailAddress,
        isAdding,
      }),
    [defaultValues, isAdult, isAdding],
  );
  const phoneFieldRules = useMemo(
    () =>
      getPhoneFieldRules({
        isAdult,
        phoneNumber: defaultValues.generalInformation.phoneNumber,
        isAdding,
      }),
    [defaultValues, isAdult, isAdding],
  );
  const applicantTypeFieldRules = useMemo(
    () =>
      getApplicantTypeFieldRules(
        defaultValues.generalInformation.applicantTypeId,
        isAdding,
      ),
    [defaultValues, isAdding],
  );
  const relationshipFieldRules = useMemo(
    () =>
      getRelationshipFieldRules({
        isPrimary,
        isMinor: !isAdult,
        relationshipId: defaultValues.generalInformation.relationshipId,
        isAdding,
        formatMessage,
      }),
    [defaultValues, isPrimary, isAdult, isAdding, formatMessage],
  );

  const sections = useMemo(() => {
    const isAddingAdult = isAdding && isAdult;
    // Required on both Add paths, not just the applicant one. The backends
    // do differ -- updateApplicants (Applications.js#checkAffordableRelation)
    // throws "Affordable Relation is required for Affordable households" on a
    // null value, while the resident Add flow (Residents.js#addAdultApplicant)
    // only checks the key `if ('affordableRelationshipId' in data)` -- but the
    // legacy household form asked for it on both, for adults and minors alike
    // (EditHouseholdForm/validate.js), enforcing it in the form rather than
    // the API. Leaving the resident path optional dropped a compliance field
    // the page it replaces had always collected.
    const affordableRelationshipRequiredOnAdd = isAddingAdult && isAffordable;
    // Required on edit too, or a cleared value reaches the API as an unreadable 400.
    const affordableRelationshipRequired =
      affordableRelationshipRequiredOnAdd ||
      (isPending && isAdult && isAffordable);

    const affordableRelationshipOptions = affordableRelationships.filter((ar) =>
      isAdult ? ar?.isAdult : ar?.isChild,
    );

    // Only the actual Head of Household (isPrimary, ResidentCustomer/
    // Household.mainCustomerId) can carry this Affordable Relation value --
    // a household has exactly one. Disabled rather than removed, same
    // lookup-by-text approach the legacy Edit Household form
    // (AdultFields.js) already uses for this same option.
    const affordableRelationshipOptionsWithHeadOfHouseholdLocked =
      affordableRelationshipOptions.map((option) =>
        option.text === 'Head of Household' && !isPrimary
          ? { ...option, disabled: true }
          : option,
      );

    // Guarantor Lease Signer is unavailable to the Head of Household, and to
    // anyone already carrying the Head of Household affordable relation --
    // which is the same member mid-edit, before the two have been saved back
    // into agreement. Looked up by text like the option above, since the id
    // itself is data.
    const headOfHouseholdAffordableRelationshipId =
      affordableRelationshipOptions.find(
        (option) => option.text === 'Head of Household',
      )?.value ?? null;
    // ENG-32608: the Head of Household can never be a Guarantor Lease Signer,
    // a compliance rule independent of the epic flag -- so isPrimary disables
    // the option unconditionally. The cascade-driven case (a member already
    // carrying the Head of Household affordable relation) stays flag-gated,
    // since that only exists alongside the Type cascade.
    const isGuarantorOptionDisabled =
      isPrimary ||
      (isEditHouseholdMemberDatesEnabled &&
        Boolean(headOfHouseholdAffordableRelationshipId) &&
        currentAffordableRelationshipId ===
          headOfHouseholdAffordableRelationshipId);

    // 'Aged up Minor' is set by the age-up process, never picked by hand --
    // disabled rather than removed, so a member that already carries it still
    // displays its own type instead of an empty field (MUI blocks selecting a
    // disabled MenuItem, the same mechanism DEFAULT_OPTION relies on above).
    const applicantTypeOptionsWithReservedDisabled = applicantTypeOptions.map(
      (option) => {
        if (option.value === agedUpMinorApplicantTypeId) {
          return { ...option, disabled: true };
        }
        // ENG-32608: the Head of Household cannot hold a non-financially-
        // responsible adult type -- Live-In-Caretaker, Non Financial Lease
        // Signer, Aged up Minor. Guarantor is financially responsible yet
        // still barred, and is handled by the isGuarantorOptionDisabled gate
        // below. Mirrors changeHeadOfHousehold's server-side eligibility rule
        // (financiallyResponsible && !Guarantor) rather than a hard-coded id
        // list, so the two paths can't drift. Unconditional per the defect's
        // AC -- the exclusion is not tied to the epic flag.
        if (isPrimary && option.financiallyResponsible === false) {
          return { ...option, disabled: true };
        }
        if (
          isGuarantorOptionDisabled &&
          guarantorApplicantTypeId &&
          option.value === guarantorApplicantTypeId
        ) {
          return { ...option, disabled: true };
        }
        return option;
      },
    );

    // Picking Guarantor Lease Signer or Live-In Caretaker forces that type's
    // paired Relation + Affordable Relation, and moving off one clears both
    // back to blank.
    //
    // Expressed as the Form library's own `changeActions` rather than an
    // `onChange` on the field: Form.Input's buildInput assigns
    // react-hook-form's own handler to `onChange` AFTER spreading the field
    // config, so an onChange declared here is silently dropped and would
    // never run (unlike onFocus/onBlur, which it does chain through).
    // changeActions is the supported hook for exactly this, already used
    // across LeaseApplicationForm/schema.
    //
    // Actions apply in array order, so the two unconditional clears are
    // overwritten by the matching set actions whenever the newly-picked type
    // is itself cascaded. The clears are only present at all while a
    // cascaded type is the CURRENT selection, which is what reproduces the
    // legacy form's prevType guard: moving between two non-cascaded types
    // must leave a hand-picked Relation alone.
    const cascadedTypePairs = [
      {
        applicantTypeId: guarantorApplicantTypeId,
        relationshipId: guarantorRelationshipId,
        affordableRelationshipId: guarantorAffordableRelationshipId,
      },
      {
        applicantTypeId: liveInCaretakerApplicantTypeId,
        relationshipId: liveInCaretakerRelationshipId,
        affordableRelationshipId: liveInCaretakerAffordableRelationshipId,
      },
    ].filter((pair) => Boolean(pair.applicantTypeId));
    const applicantTypeChangeActions =
      isEditHouseholdMemberDatesEnabled && cascadedTypePairs.length > 0
        ? [
            ...(isCascadedApplicantType
              ? [
                  {
                    type: 'updateField',
                    target: 'generalInformation.relationshipId',
                    value: CASCADE_CLEARED_VALUE,
                  },
                  {
                    type: 'updateField',
                    target: 'affordableInformation.affordableRelationshipId',
                    value: CASCADE_CLEARED_VALUE,
                  },
                ]
              : []),
            ...cascadedTypePairs.flatMap((pair) => [
              {
                type: 'updateField',
                target: 'generalInformation.relationshipId',
                when: { eq: pair.applicantTypeId },
                value: pair.relationshipId ?? CASCADE_CLEARED_VALUE,
              },
              {
                type: 'updateField',
                target: 'affordableInformation.affordableRelationshipId',
                when: { eq: pair.applicantTypeId },
                value: pair.affordableRelationshipId ?? CASCADE_CLEARED_VALUE,
              },
            ]),
          ]
        : null;

    const generalFields = [
      // Legal First, Middle, Last, then Preferred. Shared by the Add modal and the Mini-Profile.
      {
        variant: 'text',
        name: 'generalInformation.firstName',
        label: 'Legal First Name',
        rules: { required: true },
        GridProps: { xs: 12, md: 6 },
      },
      {
        variant: 'text',
        name: 'generalInformation.middleName',
        label: 'Middle Name',
        GridProps: { xs: 12, md: 6 },
      },
      {
        variant: 'text',
        name: 'generalInformation.lastName',
        label: 'Last Name',
        rules: { required: true },
        GridProps: { xs: 12, md: 6 },
      },
      {
        variant: 'text',
        name: 'generalInformation.preferredName',
        label: 'Preferred Name',
        GridProps: { xs: 12, md: 6 },
      },
      {
        // Unlike Relation/Type below, Suffix isn't required at all — nothing
        // else here should stop a user from clearing it back out.
        variant: 'singleSelect',
        name: 'generalInformation.suffixId',
        label: 'Suffix',
        ...clearableSelectProps(suffixes),
        placeholder: 'Choose',
        GridProps: { xs: 12, md: 6 },
      },
      ...(!isAdult
        ? [
            {
              variant: 'singleSelect',
              name: 'generalInformation.relationshipId',
              label: 'Relation',
              options: withChooseOption(minorRelationshipOptions),
              placeholder: 'Choose',
              rules: relationshipFieldRules,
              // A minor's Relation is edited nowhere else: the application
              // form is adults-only, so pointing at it would send the user
              // somewhere that cannot set this. Pending never means a minor
              // on the resident side, so dropping it here changes nothing
              // there.
              disabled: isPrimary,
              helperText: isPrimary
                ? formatMessage(messages.headOfHouseholdRelationHelperText)
                : undefined,
              GridProps: { xs: 12, md: 6 },
            },
          ]
        : []),
      // Kept next to Suffix so General Information reads Suffix|Type,
      // Start Date|End Date, Phone|Phone Type, Email Address|Email Type,
      // with Relation on its own at the end.
      ...(isAdult
        ? [
            {
              variant: 'singleSelect',
              name: 'generalInformation.applicantTypeId',
              label: 'Type',
              options: withChooseOption(
                applicantTypeOptionsWithReservedDisabled,
              ),
              placeholder: 'Choose',
              rules: applicantTypeFieldRules,
              ...(applicantTypeChangeActions
                ? { changeActions: applicantTypeChangeActions }
                : {}),
              // ENG-32608 reopens Type for the Head of Household, with the
              // restricted adult types disabled in the option list above. Left
              // editable unconditionally (the defect's AC is not flag-gated);
              // an open certification still locks it through the cert
              // wrapper (affordable only, once the flag is on).
              disabled: false,
              GridProps: { xs: 12, md: 6 },
            },
          ]
        : []),

      {
        variant: 'date',
        name: 'generalInformation.startDate',
        label: 'Start Date',
        // Required for Current/Prior, but never populated for a Pending
        // Applicant (no Resident row yet, so no startDate to speak of) —
        // required + disabled + empty made the form permanently invalid,
        // keeping Save disabled forever for any Pending Applicant. Same
        // reasoning applies to Add mode — a brand-new member has no
        // startDate to speak of either.
        rules:
          isPending || isAdding
            ? { validate: startDateValidate }
            : { required: true, validate: startDateValidate },
        disabled: startDateDisabled,
        helperText: startDateHelperText,
        GridProps: { xs: 12, md: 6 },
      },
      {
        variant: 'date',
        name: 'generalInformation.endDate',
        label: 'End Date',
        rules: { validate: endDateValidate },
        disabled: endDateDisabled,
        helperText: endDateHelperText,
        GridProps: { xs: 12, md: 6 },
      },
      // Phone and Phone Type, adults only -- a minor has no Customer record/
      // contact columns to save a phone number to, and Phone Number never
      // had a Type dropdown of its own. Not disabled for Pending -- there's
      // no reason to block the Head of Household from picking their own
      // phone type, and the backend now persists it. Phone Type has no
      // blank/"Choose" option (unlike every other SingleSelect in this
      // file) -- getDefaultValues above already seeds this to Mobile
      // whenever there's no saved type, so the field is never actually
      // unset; adding a "Choose" placeholder back would just let the user
      // pick an unset state that has no real meaning to block Save on.
      ...(isAdult
        ? [
            {
              variant: 'phone',
              name: 'generalInformation.phoneNumber',
              label: 'Phone',
              rules: phoneFieldRules,
              GridProps: { xs: 12, md: 6 },
            },
            {
              variant: 'singleSelect',
              name: 'generalInformation.phoneType',
              label: 'Phone Type',
              options: PHONE_TYPES,
              GridProps: { xs: 12, md: 6 },
            },
          ]
        : []),
      // Secondary/Tertiary Phone and Email, adults only (omitted entirely
      // for minors, not merely disabled -- same array-spread idiom as
      // Type/Relation above, not a `disabled` flag). Also shown while
      // creating a brand-new adult: buildSavePayload's create branch
      // assembles this into the legacy create endpoint's
      // `data.contactInformation.{emails,phones}` shape (full array
      // including the primary, `isPrimary` set explicitly) -- a different
      // shape from the update paths' `additionalPhones`/`additionalEmails`
      // (additional-only, no `isPrimary`).
      // No `disabled`/read-only wrapper of any kind, unlike Type/Relation/
      // Dates above -- General Information is deliberately the one section
      // with no withReadOnlyIfPendingOrAdding wrapper, which is what
      // lets Current, Pending, and Prior adults all edit these. Gated
      // behind the epic-wide `edit-household-member-dates` flag: off
      // renders nothing here at all.
      //
      // Revealed one at a time via an "Add" link rather than shown as two
      // permanently-empty fields, matching the legacy EditResidentHousehold
      // form's ContactFieldArray's Primary/Secondary/Tertiary pattern --
      // same ordinal vocabulary, same one-primary-plus-up-to-2-additional
      // cap, same "only the last slot can be removed" rule.
      //
      // Each Secondary/Tertiary pair keeps the same `hidden` condition on
      // both its value and Type field (never split), so the pair is always
      // either fully in the grid or fully out of it -- preserving row-
      // parity in every reveal state. The Add/Remove row is a single
      // xs:12 custom field for the same reason: only one of Add/Remove
      // ever applies at once, and folding both into one always-full-width
      // field avoids a lone half-width item breaking parity for whatever
      // follows.
      //
      // Hiding a revealed field relies on the Form library's own
      // hidden-field lifecycle (clears the RHF value, then Collapse's
      // unmountOnExit drops it from the grid) -- Add/Remove only ever
      // change the revealed count; they never touch field values directly.
      ...(isAdult && isEditHouseholdMemberDatesEnabled
        ? [
            {
              variant: 'phone',
              name: 'generalInformation.secondaryPhone.phoneNumber',
              label: 'Secondary Phone',
              rules: ADDITIONAL_PHONE_FIELD_RULES,
              hidden: isSecondaryPhoneRowHidden,
              GridProps: additionalValueGridProps(isSecondaryPhoneRowHidden),
            },
            {
              variant: 'singleSelect',
              name: 'generalInformation.secondaryPhone.phoneType',
              label: 'Secondary Phone Type',
              options: ADDITIONAL_PHONE_TYPE_OPTIONS,
              hidden: isSecondaryPhoneRowHidden,
              GridProps: additionalTypeGridProps(
                isSecondaryPhoneRowHidden,
                secondaryPhoneShowsTrash,
              ),
            },
            // The trash icon is a 3rd column beside the row currently
            // last, not a shared button below everything. Secondary is
            // only "last" when Tertiary hasn't been revealed (count === 1);
            // Tertiary, once visible at all, always is (the cap is 2). See
            // additionalTrashGridProps for the width/padding handling.
            {
              variant: 'custom',
              name: 'secondaryPhoneRemove',
              component: secondaryPhoneShowsTrash ? (
                <IconButton
                  aria-label="Remove Secondary Phone"
                  color="primary"
                  onClick={() => {
                    setRevealedAdditionalPhoneCount(0);
                    clearAdditionalPhoneSlot?.('secondary');
                  }}
                >
                  <TrashIcon />
                </IconButton>
              ) : null,
              GridProps: additionalTrashGridProps(secondaryPhoneShowsTrash),
            },
            {
              variant: 'phone',
              name: 'generalInformation.tertiaryPhone.phoneNumber',
              label: 'Tertiary Phone',
              rules: ADDITIONAL_PHONE_FIELD_RULES,
              hidden: isTertiaryPhoneRowHidden,
              GridProps: additionalValueGridProps(isTertiaryPhoneRowHidden),
            },
            {
              variant: 'singleSelect',
              name: 'generalInformation.tertiaryPhone.phoneType',
              label: 'Tertiary Phone Type',
              options: ADDITIONAL_PHONE_TYPE_OPTIONS,
              hidden: isTertiaryPhoneRowHidden,
              GridProps: additionalTypeGridProps(
                isTertiaryPhoneRowHidden,
                tertiaryPhoneShowsTrash,
              ),
            },
            // Tertiary is only ever shown once revealedPhoneCount === 2
            // (the cap), so whenever it's visible at all it IS the last
            // slot -- no count check needed, unlike Secondary's above.
            {
              variant: 'custom',
              name: 'tertiaryPhoneRemove',
              component: tertiaryPhoneShowsTrash ? (
                <IconButton
                  aria-label="Remove Tertiary Phone"
                  color="primary"
                  onClick={() => {
                    setRevealedAdditionalPhoneCount(1);
                    clearAdditionalPhoneSlot?.('tertiary');
                  }}
                >
                  <TrashIcon />
                </IconButton>
              ) : null,
              GridProps: additionalTrashGridProps(tertiaryPhoneShowsTrash),
            },
            {
              variant: 'custom',
              name: 'additionalPhoneActions',
              hidden: revealedPhoneCount >= 2,
              component: (
                <Button
                  variant="text"
                  startIcon={<AddIcon />}
                  onClick={() =>
                    setRevealedAdditionalPhoneCount(
                      Math.min(revealedPhoneCount + 1, 2),
                    )
                  }
                >
                  Add Phone Number
                </Button>
              ),
              GridProps: { xs: 12 },
            },
          ]
        : []),
      // Add Phone Number sits between the Phone and Email rows (the whole
      // phone cluster finishes before Email starts); Add Email Address
      // sits right after the Email row. Splitting the single flag-gated
      // array above into two (phone-cluster / email-cluster) makes room
      // for the primary Email/Email Type pair to sit between them.
      // Email Address and Email Type, adults only -- same reasoning as
      // Phone/Phone Type above (no Customer record/contact columns for a
      // minor's email, no separate Type concept without one). Not gated by
      // `edit-household-member-dates` -- that flag only governs Secondary/
      // Tertiary, matching Phone/Phone Type's own gating above.
      ...(isAdult
        ? [
            {
              variant: 'email',
              name: 'generalInformation.emailAddress',
              label: 'Email Address',
              rules: emailFieldRules,
              GridProps: { xs: 12, md: 6 },
            },
            {
              variant: 'singleSelect',
              name: 'generalInformation.emailType',
              label: 'Email Type',
              options: EMAIL_TYPES,
              GridProps: { xs: 12, md: 6 },
            },
          ]
        : []),
      ...(isAdult && isEditHouseholdMemberDatesEnabled
        ? [
            {
              variant: 'email',
              name: 'generalInformation.secondaryEmail.emailAddress',
              label: 'Secondary Email',
              rules: ADDITIONAL_EMAIL_FIELD_RULES,
              hidden: isSecondaryEmailRowHidden,
              GridProps: additionalValueGridProps(isSecondaryEmailRowHidden),
            },
            {
              variant: 'singleSelect',
              name: 'generalInformation.secondaryEmail.emailType',
              label: 'Secondary Email Type',
              options: ADDITIONAL_EMAIL_TYPE_OPTIONS,
              hidden: isSecondaryEmailRowHidden,
              GridProps: additionalTypeGridProps(
                isSecondaryEmailRowHidden,
                secondaryEmailShowsTrash,
              ),
            },
            // Same 3rd-column trash icon as the Phone block above -- only
            // reserves its md:1 share (collapsing to md:0 otherwise) on the
            // current last slot; padding zeroed together with the row when
            // fully hidden.
            {
              variant: 'custom',
              name: 'secondaryEmailRemove',
              component: secondaryEmailShowsTrash ? (
                <IconButton
                  aria-label="Remove Secondary Email"
                  color="primary"
                  onClick={() => {
                    setRevealedAdditionalEmailCount(0);
                    clearAdditionalEmailSlot?.('secondary');
                  }}
                >
                  <TrashIcon />
                </IconButton>
              ) : null,
              GridProps: additionalTrashGridProps(secondaryEmailShowsTrash),
            },
            {
              variant: 'email',
              name: 'generalInformation.tertiaryEmail.emailAddress',
              label: 'Tertiary Email',
              rules: ADDITIONAL_EMAIL_FIELD_RULES,
              hidden: isTertiaryEmailRowHidden,
              GridProps: additionalValueGridProps(isTertiaryEmailRowHidden),
            },
            {
              variant: 'singleSelect',
              name: 'generalInformation.tertiaryEmail.emailType',
              label: 'Tertiary Email Type',
              options: ADDITIONAL_EMAIL_TYPE_OPTIONS,
              hidden: isTertiaryEmailRowHidden,
              GridProps: additionalTypeGridProps(
                isTertiaryEmailRowHidden,
                tertiaryEmailShowsTrash,
              ),
            },
            // Tertiary is only ever shown once revealedEmailCount === 2
            // (the cap), so whenever visible it IS the last slot.
            {
              variant: 'custom',
              name: 'tertiaryEmailRemove',
              component: tertiaryEmailShowsTrash ? (
                <IconButton
                  aria-label="Remove Tertiary Email"
                  color="primary"
                  onClick={() => {
                    setRevealedAdditionalEmailCount(1);
                    clearAdditionalEmailSlot?.('tertiary');
                  }}
                >
                  <TrashIcon />
                </IconButton>
              ) : null,
              GridProps: additionalTrashGridProps(tertiaryEmailShowsTrash),
            },
            {
              variant: 'custom',
              name: 'additionalEmailActions',
              hidden: revealedEmailCount >= 2,
              component: (
                <Button
                  variant="text"
                  startIcon={<AddIcon />}
                  onClick={() =>
                    setRevealedAdditionalEmailCount(
                      Math.min(revealedEmailCount + 1, 2),
                    )
                  }
                >
                  Add Email Address
                </Button>
              ),
              GridProps: { xs: 12 },
            },
          ]
        : []),
      // Relation is the last field before the caption, after the
      // Secondary/Tertiary block too -- nothing after it but the
      // full-width caption below, so no parity concern.
      ...(isAdult
        ? [
            {
              variant: 'singleSelect',
              name: 'generalInformation.relationshipId',
              label: 'Relation',
              options: withChooseOption(relationshipOptions),
              placeholder: 'Choose',
              rules: relationshipFieldRules,
              // Guarantor Lease Signer / Live-In Caretaker own this value
              // (see the cascade above); editing it by hand would let it
              // drift out of sync with the Type that set it.
              disabled: isPrimary || isCascadedApplicantType,
              helperText: isPrimary
                ? formatMessage(messages.headOfHouseholdRelationHelperText)
                : undefined,
              GridProps: { xs: 12, md: 6 },
            },
          ]
        : []),
      ...(isAdult
        ? [
            {
              variant: 'caption',
              children: `How did you hear about our property? ${referralType}`,
              GridProps: { xs: 12 },
            },
          ]
        : []),
    ];

    // A conventional minor is held to no identification rule -- nothing
    // qualifies their household on one -- so neither field carries the
    // conditionally-required icon or the helper text that explains it.
    const identificationMatters = isAdult || isAffordable;
    const identificationIconFor = (fieldKey) => (label) =>
      identificationMatters ? withIdentificationIcon(fieldKey)(label) : label;
    const identificationHelperFor = (fieldKey) =>
      identificationMatters ? identificationHelperTextFor(fieldKey) : undefined;

    const ssnField = {
      variant: 'socialSecurity',
      name: 'personalInformation.socialSecurityNumber',
      label: identificationIconFor('socialSecurityNumber')('SSN or TIN'),
      helperText: identificationHelperFor('socialSecurityNumber'),
      rules: ssnFieldRules,
      GridProps: { xs: 12, md: 6 },
    };

    // Removing a license/passport number clears its State/Country with it;
    // the reverse is left to the pair rule, which blocks Save instead.
    const personalAdultFields = [
      {
        // Conditionally required, not hard-required — incomplete
        // conditionally-required fields must not block Save.
        variant: 'singleSelect',
        name: 'personalInformation.maritalStatus',
        label: withConditionallyRequiredIcon('Marital Status'),
        ...clearableSelectProps(maritalStatuses),
        placeholder: 'Choose',
        GridProps: { xs: 12, md: 6 },
      },
      {
        variant: 'date',
        name: 'personalInformation.birthday',
        label: withConditionallyRequiredIcon('Date of Birth'),
        rules: { validate: dateOfBirthValidate },
        disableFuture: true,
        GridProps: { xs: 12, md: 6 },
      },
      {
        variant: 'text',
        name: 'personalInformation.stateIdNumber',
        label: withIdentificationIcon('stateIdNumber')(
          "Driver's License / State ID #",
        ),
        helperText: identificationHelperTextFor('stateIdNumber'),
        rules: identificationPairRules.stateIdNumber,
        changeActions: [
          {
            type: 'updateField',
            target: 'personalInformation.stateIdOrigin',
            when: { eq: '' },
            value: '',
          },
        ],
        watchFields: ['personalInformation.stateIdOrigin'],
        GridProps: { xs: 12, md: 6 },
      },
      {
        variant: 'singleSelect',
        name: 'personalInformation.stateIdOrigin',
        label: withIdentificationIcon('stateIdOrigin')(
          "Driver's License / State ID State",
        ),
        helperText: identificationHelperTextFor('stateIdOrigin'),
        rules: identificationPairRules.stateIdOrigin,
        watchFields: ['personalInformation.stateIdNumber'],
        ...clearableSelectProps(states),
        placeholder: 'Choose',
        GridProps: { xs: 12, md: 6 },
      },
      ssnField,
      {
        variant: 'text',
        name: 'personalInformation.alienRegistration',
        label:
          withIdentificationIcon('alienRegistration')('Alien Registration'),
        helperText: identificationHelperTextFor('alienRegistration'),
        GridProps: { xs: 12, md: 6 },
      },
      {
        variant: 'text',
        name: 'personalInformation.passportNumber',
        label: withIdentificationIcon('passportNumber')('Passport #'),
        helperText: identificationHelperTextFor('passportNumber'),
        rules: identificationPairRules.passportNumber,
        changeActions: [
          {
            type: 'updateField',
            target: 'personalInformation.passportCountry',
            when: { eq: '' },
            value: '',
          },
        ],
        watchFields: ['personalInformation.passportCountry'],
        GridProps: { xs: 12, md: 6 },
      },
      {
        variant: 'singleSelect',
        name: 'personalInformation.passportCountry',
        label: withIdentificationIcon('passportCountry')('Passport Country'),
        helperText: identificationHelperTextFor('passportCountry'),
        rules: identificationPairRules.passportCountry,
        watchFields: ['personalInformation.passportNumber'],
        ...clearableSelectProps(countries),
        placeholder: 'Choose',
        GridProps: { xs: 12, md: 6 },
      },
    ];

    const personalMinorFields = [
      ssnField,
      {
        // Required for minors (Excel), unlike the adult Date of Birth field.
        variant: 'date',
        name: 'personalInformation.dateOfBirth',
        label: 'Date of Birth',
        rules: { required: true, validate: dateOfBirthValidate },
        disableFuture: true,
        GridProps: { xs: 12, md: 6 },
      },
      {
        variant: 'text',
        name: 'personalInformation.alienRegistrationNumber',
        label: identificationIconFor('alienRegistrationNumber')(
          'Alien Registration #',
        ),
        helperText: identificationHelperFor('alienRegistrationNumber'),
        GridProps: { xs: 12, md: 6 },
      },
    ];

    const demographicFields = buildDemographicFields({
      isAffordable,
      isAdult,
      isPending,
      isPrimary,
      isCascadedApplicantType,
      clearableSelectProps,
      formatMessage,
      affordableRelationshipOptions:
        affordableRelationshipOptionsWithHeadOfHouseholdLocked,
      affordableRelationshipRequired,
      wasAffordableRelationshipCleared,
      disabilitySubFieldsDisabled,
      specialNeedsDesignations,
      annualIncomeValidate,
      showMultiRace,
      multiRaceOptions,
    });

    const claimedDisplay =
      portalClaimed === 'yes'
        ? formatMessage(appMessages.yes)
        : portalClaimed === 'no'
        ? formatMessage(appMessages.no)
        : '';
    // Still one row of 12, but not four equal quarters: an address needs the
    // most room and Yes/No the least, so Claimed gives its spare column to
    // Email Address. Last Login and Invite Sent both carry a date AND a time,
    // so neither can go below 3.
    const readOnlyPortalGridProps = { xs: 12, md: 3 };
    const portalFields = [
      {
        variant: 'custom',
        name: 'portalAccess.emailAddress',
        component: renderReadOnlyField({
          label: formatMessage(messages.portalEmailAddressLabel),
          value: portalEmailAddress,
        }),
        GridProps: { xs: 12, md: 4 },
      },
      {
        variant: 'custom',
        name: 'portalAccess.claimed',
        component: renderReadOnlyField({
          label: formatMessage(messages.portalClaimedLabel),
          value: claimedDisplay,
        }),
        GridProps: { xs: 12, md: 2 },
      },
      {
        variant: 'custom',
        name: 'portalAccess.lastLogin',
        component: renderReadOnlyField({
          label: formatMessage(messages.portalLastLoginLabel),
          value: portalLastLogin,
        }),
        GridProps: readOnlyPortalGridProps,
      },
      {
        variant: 'custom',
        name: 'portalAccess.inviteSent',
        component: renderReadOnlyField({
          label: formatMessage(messages.portalInviteSentLabel),
          value: portalInviteSent,
        }),
        GridProps: readOnlyPortalGridProps,
      },
    ];

    // Current members and the applicant subject (a Pending applicant has a Customer, so a portal
    // account). Never while adding: no Customer until the first Save. Read off a leaf for a stable memo.
    const showsAccountManagement =
      Boolean(portalActionOnChange) &&
      !isAdding &&
      (occupantStatus === 'Current' || isApplicantSubject);

    // Not gated on showsAccountManagement: the removed-from-household banner
    // only ever fires for a Prior member, who has no Account Management at
    // all, so gating it there would make it unreachable. Every other banner
    // requires a resolved status query, which only runs for Current.
    const bannerFields = (portalActionBanners ?? []).map((banner, index) => ({
      variant: 'banner',
      name: `portalAccess.banner.${index}`,
      status: banner.status,
      message: banner.message,
      GridProps: { xs: 12 },
    }));

    const accountManagementFields = showsAccountManagement
      ? [
          {
            variant: 'custom',
            name: 'portalAccess.accountManagementDivider',
            // The Grid's rowSpacing alone leaves the rule almost touching the
            // values above and the sub-heading below; the margin is what makes
            // it read as a separator rather than another row.
            component: <Divider sx={{ my: 2 }} />,
            GridProps: { xs: 12 },
          },
          {
            variant: 'formSubheading',
            name: 'portalAccess.accountManagementHeading',
            children: formatMessage(messages.portalAccountManagementHeading),
            GridProps: { xs: 12 },
          },
          {
            variant: 'custom',
            name: 'portalAccess.accountAction',
            // Deliberately outside react-hook-form: selecting an action is a
            // trigger, not form data, and registering it would mark the form
            // dirty and feed the Save gate.
            component: (
              <SingleSelect
                name="portalAccountAction"
                options={portalActionOptions}
                value={portalActionSelectedAction}
                placeholder={formatMessage(messages.portalSelectAnAction)}
                // placeholder is only a visual hint, so the combobox would
                // otherwise reach a screen reader with no name at all.
                inputProps={{
                  'aria-label': formatMessage(messages.portalSelectAnAction),
                }}
                displayEmpty
                fullWidth
                onChange={portalActionOnChange}
              />
            ),
            // Two of the row's four slots — the option labels ("Send Portal
            // Invite", "Reset Login Attempts") do not fit in one.
            GridProps: { xs: 12, md: 6 },
          },
        ]
      : [];

    // Personal/Affordable belong to the application while the member is (or is
    // about to become) a Pending Applicant. Only what the application never
    // asks for stays editable here, plus the minor's required Date of Birth.
    // A minor's Affordable Relation is set once, on Add; an adult's stays open.
    const STILL_EDITABLE_WHILE_READ_ONLY = [
      'affordableInformation.specialNeedsDesignationIds',
      'personalInformation.dateOfBirth',
      'personalInformation.annualIncome',
      ...(isAdult || isAdding
        ? ['affordableInformation.affordableRelationshipId']
        : []),
      // A conventional application never asks a minor for either, so this is
      // the only place to enter them -- on Edit or Add. Affordable asks, and
      // keeps them.
      ...(!isAdult && !isAffordable
        ? [
            'personalInformation.socialSecurityNumber',
            'personalInformation.alienRegistrationNumber',
          ]
        : []),
    ];
    const withReadOnlyIfPendingOrAdding = (fields) =>
      isPending || isAdding
        ? fields.map((field) =>
            STILL_EDITABLE_WHILE_READ_ONLY.includes(field.name)
              ? field
              : { ...field, disabled: true },
          )
        : fields;

    // What an open certification freezes. Composes with the Pending wrapper
    // -- either reason disables, neither un-disables. blockedNames omitted
    // means the whole section, empty means none of it. The message overrides
    // a blocked field's own helper text: while the lock holds, the
    // certification is the only reason the user can act on. Start and End
    // Date are no-ops here -- their own chain already set the same text.
    const withReadOnlyIfCertLocked = (fields, blockedNames) =>
      certLock
        ? fields.map((field) =>
            !blockedNames || blockedNames.includes(field.name)
              ? {
                  ...field,
                  disabled: true,
                  helperText: formatMessage(messages.openCertHelperText),
                  // A blank Relation that cannot be edited must not block Save.
                  ...(field.name === 'generalInformation.relationshipId' && {
                    rules: undefined,
                  }),
                }
              : field,
          )
        : fields;

    const certLockedGeneralFields = certLockedGeneralFieldsFor({
      isNarrowedToComposition: isEditHouseholdMemberDatesEnabled,
      isAffordable,
      isAdult,
    });
    // Personal describes the person, never the certified composition.
    const certLockedPersonalFields = isEditHouseholdMemberDatesEnabled
      ? CERT_LOCKED_NO_FIELDS
      : undefined;
    const certLockedDemographicFields = certLockedDemographicFieldsFor({
      isNarrowedToComposition: isEditHouseholdMemberDatesEnabled,
      isAffordable,
      isAdult,
      hasLockedOpenCert,
      lockInfoPending,
    });

    // No cert-lock or Pending/Adding wrapper on the commercial list: neither
    // concept exists for a commercial household.
    const generalSection = {
      id: 'general',
      navigationLabel: 'General',
      sectionTitle: 'General Information',
      fields: isCommercial
        ? buildCommercialGeneralFields({
            states,
            phoneFieldRules,
            emailFieldRules,
            annualIncomeValidate,
            clearableSelectProps,
            formatMessage,
          })
        : withReadOnlyIfCertLocked(generalFields, certLockedGeneralFields),
    };
    const personalSection = {
      id: 'personal',
      navigationLabel: 'Personal',
      sectionTitle: 'Personal Information',
      fields: withReadOnlyIfCertLocked(
        withReadOnlyIfPendingOrAdding(
          isAdult ? personalAdultFields : personalMinorFields,
        ),
        certLockedPersonalFields,
      ),
    };
    const demographicSection = {
      id: 'demographic',
      navigationLabel: 'Demographics',
      sectionTitle: 'Demographic Information',
      fields: withReadOnlyIfCertLocked(
        withReadOnlyIfPendingOrAdding(demographicFields),
        certLockedDemographicFields,
      ),
    };
    const portalSection = {
      id: 'portal',
      navigationLabel: 'Portal',
      sectionTitle: 'Portal Access',
      // Banners lead: they state the account's status, which frames the
      // read-only values rather than following from them. Also the only
      // sensible position for a Prior member, whose section has nothing
      // after the four fields for a trailing banner to sit above.
      fields: [...bannerFields, ...portalFields, ...accountManagementFields],
    };

    if (isCommercial) {
      return [generalSection, portalSection];
    }
    return [
      generalSection,
      personalSection,
      demographicSection,
      ...(isAdult ? [portalSection] : []),
    ];
  }, [
    affordableRelationships,
    annualIncomeValidate,
    showMultiRace,
    multiRaceOptions,
    applicantTypeFieldRules,
    applicantTypeOptions,
    agedUpMinorApplicantTypeId,
    clearAdditionalEmailSlot,
    clearAdditionalPhoneSlot,
    certLock,
    countries,
    currentAffordableRelationshipId,
    guarantorApplicantTypeId,
    guarantorRelationshipId,
    guarantorAffordableRelationshipId,
    hasLockedOpenCert,
    lockInfoPending,
    isCascadedApplicantType,
    wasAffordableRelationshipCleared,
    liveInCaretakerApplicantTypeId,
    liveInCaretakerRelationshipId,
    liveInCaretakerAffordableRelationshipId,
    dateOfBirthValidate,
    disabilitySubFieldsDisabled,
    startDateDisabled,
    startDateHelperText,
    emailFieldRules,
    endDateDisabled,
    endDateHelperText,
    endDateValidate,
    formatMessage,
    identificationHelperTextFor,
    isAdding,
    isAdult,
    isAffordable,
    isCommercial,
    isEditHouseholdMemberDatesEnabled,
    isPending,
    isPrimary,
    isSecondaryEmailRowHidden,
    isSecondaryPhoneRowHidden,
    isTertiaryEmailRowHidden,
    isTertiaryPhoneRowHidden,
    occupantStatus,
    phoneFieldRules,
    isApplicantSubject,
    portalActionBanners,
    portalActionOnChange,
    portalActionOptions,
    portalActionSelectedAction,
    portalClaimed,
    portalEmailAddress,
    portalInviteSent,
    portalLastLogin,
    referralType,
    relationshipFieldRules,
    relationshipOptions,
    minorRelationshipOptions,
    revealedEmailCount,
    revealedPhoneCount,
    secondaryEmailShowsTrash,
    secondaryPhoneShowsTrash,
    setRevealedAdditionalEmailCount,
    setRevealedAdditionalPhoneCount,
    specialNeedsDesignations,
    ssnFieldRules,
    identificationPairRules,
    startDateValidate,
    states,
    suffixes,
    tertiaryEmailShowsTrash,
    tertiaryPhoneShowsTrash,
    withIdentificationIcon,
  ]);

  return {
    defaultValues,
    sections,
    // Unlike every other conditionally-required field, this one actually
    // blocks Save once every identification field is empty at once
    // (MiniProfileBridge/index.js wires this into the Save button).
    hasAtLeastOneIdentification,
    hadIdentificationOnLoad,
    // Blocks Save when a previously-saved value in one of the other
    // conditionally-required fields gets cleared back out (see above).
    hasClearedRequiredField,
    // Whether the whole Personal section is disabled by the open-cert lock
    // -- MiniProfileBridge/index.js needs this too, as a 4th exemption on
    // isSaveBlockedByMissingIdentification: an adult with no identification
    // on record has no way to supply one while Personal is disabled. The
    // exemption follows the flag, or an open cert alone bypasses the gate.
    isPersonalCertLocked: certLock && !isEditHouseholdMemberDatesEnabled,
  };
};

export default useMiniProfileSections;
